import type { AccountFormValues } from '@/components/accounts/AccountForm';
import {
  Banknote,
  BarChart3,
  Bitcoin,
  CircleDot,
  Home,
  Layers,
  PieChart,
  type LucideIcon,
} from 'lucide-react';

export type AddAccountCategoryId =
  | 'bank'
  | 'investment'
  | 'crypto'
  | 'equity'
  | 'private'
  | 'fund'
  | 'realEstate';

export type AddAccountCategory = {
  id: AddAccountCategoryId;
  icon: LucideIcon;
  /** Semantic `fin-*` token used for the icon tile. */
  tone: 'green' | 'accent' | 'cyan' | 'blue' | 'purple' | 'info';
  /**
   * Account types offered in the manual form. The first one is preselected, so
   * order matters — it is the type suggested for this category.
   */
  types: AccountFormValues['type'][];
  /**
   * Plaid products the institution must support for the automated path. Empty
   * means Plaid cannot cover this category, so only manual entry is offered.
   */
  plaidProducts: string[];
};

/**
 * Catalog behind the add-account palette: each category suggests an account
 * type and says whether it can be connected automatically or only typed in.
 * Copy lives in `accounts.add.categories.*`; this file stays presentation-free
 * so the mapping can be unit tested.
 */
export const ADD_ACCOUNT_CATEGORIES: AddAccountCategory[] = [
  {
    id: 'bank',
    icon: Banknote,
    tone: 'green',
    types: ['checking', 'savings', 'bank', 'liability'],
    plaidProducts: ['transactions'],
  },
  {
    id: 'investment',
    icon: BarChart3,
    tone: 'accent',
    types: ['investment', 'retirement'],
    plaidProducts: ['investments'],
  },
  { id: 'crypto', icon: Bitcoin, tone: 'cyan', types: ['crypto'], plaidProducts: [] },
  { id: 'equity', icon: PieChart, tone: 'blue', types: ['alternative'], plaidProducts: [] },
  { id: 'private', icon: Layers, tone: 'purple', types: ['alternative'], plaidProducts: [] },
  { id: 'fund', icon: CircleDot, tone: 'info', types: ['alternative'], plaidProducts: [] },
  { id: 'realEstate', icon: Home, tone: 'purple', types: ['real-estate'], plaidProducts: [] },
];

/** Institutions offered as one-click chips before the user types anything. */
export const POPULAR_INSTITUTIONS: { name: string; categoryId: AddAccountCategoryId }[] = [
  { name: 'Chase', categoryId: 'bank' },
  { name: 'Monzo', categoryId: 'bank' },
  { name: 'Revolut', categoryId: 'bank' },
  { name: 'Wealthfront', categoryId: 'investment' },
  { name: 'Robinhood', categoryId: 'investment' },
];

/** Trade add-account palette: investment + crypto (covers TRADING_ACCOUNT_TYPES). */
export const TRADING_ADD_ACCOUNT_CATEGORY_IDS: AddAccountCategoryId[] = ['investment', 'crypto'];

export const TRADING_ADD_ACCOUNT_CATEGORIES: AddAccountCategory[] = ADD_ACCOUNT_CATEGORIES.filter(
  (entry) => TRADING_ADD_ACCOUNT_CATEGORY_IDS.includes(entry.id)
);

/** Popular chips whose category is in the given catalog (e.g. trade-scoped home). */
export function filterPopularInstitutions(
  institutions: typeof POPULAR_INSTITUTIONS,
  categories: AddAccountCategory[]
): typeof POPULAR_INSTITUTIONS {
  const allowed = new Set(categories.map((entry) => entry.id));
  return institutions.filter((institution) => allowed.has(institution.categoryId));
}

export const getAddAccountCategory = (id: AddAccountCategoryId): AddAccountCategory => {
  const category = ADD_ACCOUNT_CATEGORIES.find((entry) => entry.id === id);
  if (!category) throw new Error(`Unknown add-account category: ${id}`);
  return category;
};

/** Categories whose title/subtitle/type labels match the typed query. */
export function filterAddAccountCategories(
  categories: AddAccountCategory[],
  query: string,
  labelsFor: (category: AddAccountCategory) => string[]
): AddAccountCategory[] {
  const needle = query.trim().toLowerCase();
  if (!needle) return categories;
  return categories.filter((category) =>
    labelsFor(category).some((label) => label.toLowerCase().includes(needle))
  );
}

/** `true` when the category can be connected through Plaid in this environment. */
export const supportsAutomatedConnect = (category: AddAccountCategory, plaidEnabled: boolean) =>
  plaidEnabled && category.plaidProducts.length > 0;
